import { IconApps, IconBed, IconBriefcase, IconCar, IconCheck, IconLogout, IconMenu2, IconPlane, IconPlugConnected, IconSelector, IconTicket, IconTrain, IconUserCog, type Icon } from "@tabler/icons-react";
import { Menu } from "@base-ui/react/menu";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "./api";
import { Drawer } from "./drawer";
import { KitProviders, Tooltip } from "./feedback";
import { initials } from "./format";
import { useNarrow } from "./responsive";
import { Select } from "./select";
import { ThemeToggle } from "./theme";

/** The Ideastation wordmark. */
export function Logo() {
  return <div className="logo">ideastat<span>io</span>n</div>;
}

/** A navigation entry; `page` is the current first route segment. */
export function NavLink({ to, page, icon, label, count }: { to: string; page: string; icon: ReactNode; label: string; count?: number | undefined }) {
  return (
    <a href={`#/${to}`} className={page === to ? "active" : ""} aria-current={page === to ? "page" : undefined}>
      {icon}<span>{label}</span>{count ? <span className="count">{count}</span> : null}
    </a>
  );
}

export function NavGroup({ children }: { children: ReactNode }) {
  return <div className="nav-group">{children}</div>;
}

/** Select in the navigation for the resource being worked on (a property, a partner…). */
export function NavSwitcher<T extends { id: string; name: string }>({ label, items, value, onChange }: { label: string; items: T[]; value: string | undefined; onChange: (id: string) => void }) {
  if (items.length === 0) return null;
  return (
    <div className="switcher">
      <div className="label">{label}</div>
      <Select aria-label={label} value={value ?? ""} onChange={onChange} options={items.map((p) => ({ value: p.id, label: p.name }))} />
    </div>
  );
}

/** A back-office the person can open (`GET /auth/apps`, central login only). */
export interface AppLink {
  key: string;
  name: string;
  url: string;
  current: boolean;
}

/** One icon per back-office; apps without one get a generic icon. */
const APP_ICONS: Record<string, Icon> = { hotels: IconBed, cars: IconCar, activities: IconTicket, rail: IconTrain, desk: IconBriefcase, flights: IconPlane, integrations: IconPlugConnected };

/**
 * The product name as a menu of the person's other back-offices, so they move between apps without going
 * through the portal. Until the list arrives, or if Resosys Access does not answer, it only links the portal.
 */
export function AppSwitcher({ product, portalUrl }: { product: string; portalUrl: string }) {
  const [apps, setApps] = useState<AppLink[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api<{ apps: AppLink[] }>("GET", "/auth/apps").then((r) => setApps(r.apps)).catch(() => setApps([]));
  }, []);

  // Base UI's Menu: arrow keys, type-ahead, Escape and a click outside close it. The popup renders inside the
  // switcher (not at the end of <body>) so that it keeps the navigation's look.
  return (
    <div className="app-switcher" ref={ref}>
      <Menu.Root>
        <Menu.Trigger className="app-switcher-button" title="Switch app">
          <span>{product}</span><IconSelector size={14} aria-hidden />
        </Menu.Trigger>
        <Menu.Portal container={ref}>
          <Menu.Positioner className="app-switcher-positioner" side="bottom" align="start" sideOffset={4}>
            <Menu.Popup className="app-switcher-menu" aria-label="Your apps">
              {apps.map((a) => {
                const AppIcon = APP_ICONS[a.key] ?? IconApps;
                return (
                  <Menu.LinkItem key={a.key} href={a.url} className={a.current ? "current" : ""} aria-current={a.current ? "page" : undefined}>
                    <AppIcon size={18} aria-hidden /><span>{a.name}</span>{a.current && <IconCheck size={16} className="check" aria-hidden />}
                  </Menu.LinkItem>
                );
              })}
              {apps.length > 0 && <Menu.Separator className="sep" />}
              <Menu.LinkItem href={portalUrl}><IconApps size={18} aria-hidden /><span>All apps</span></Menu.LinkItem>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </div>
  );
}

/**
 * The layout every extranet shares: dark navigation with the Ideastation logo, the product name (with the central
 * login, a menu of the person's other apps), the app's own links and the signed-in user (account and logout),
 * and the page next to it.
 */
export function ExtranetShell({ product, user, role, onLogout, nav, accountPath = "conta", children, portalUrl }: {
  product: string;
  /** The Resosys portal with every back-office (central login only): turns the product name into the app switcher. */
  portalUrl?: string | undefined;
  user: { name: string };
  /** Role names of the user, already joined. */
  role: string;
  onLogout: () => void;
  nav: ReactNode;
  /** Route of the account page, "conta" by default. */
  accountPath?: string;
  children: ReactNode;
}) {
  const narrow = useNarrow();
  const [menu, setMenu] = useState(false);
  // Following a link in the menu closes it.
  useEffect(() => {
    const close = () => setMenu(false);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, []);
  const switcher = portalUrl ? <AppSwitcher product={product} portalUrl={portalUrl} /> : <div className="product">{product}</div>;
  const navigation = (
    <nav className="nav" aria-label="Main navigation">
      <div className="nav-top"><Logo />{!narrow && <ThemeToggle style={{ color: "var(--nav-text)" }} />}</div>
      {switcher}
      {nav}
      <div className="user">
        <div className="avatar" aria-hidden>{initials(user.name)}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <a href={`#/${accountPath}`} className="name" style={{ padding: 0, background: "none", display: "block" }}>{user.name}</a>
          <div className="role">{role}</div>
        </div>
        <Tooltip content="Your account"><a href={`#/${accountPath}`} aria-label="Your account" style={{ padding: 6 }}><IconUserCog size={18} /></a></Tooltip>
        <Tooltip content="Sign out"><button className="ghost icon" style={{ color: "var(--nav-text)" }} onClick={onLogout} aria-label="Sign out"><IconLogout size={18} /></button></Tooltip>
      </div>
    </nav>
  );
  return (
    <KitProviders>
      {narrow ? (
        // Phones: a top bar with the menu button; the navigation opens in a drawer from the left.
        <div className="shell narrow">
          <header className="topbar">
            <button type="button" className="ghost icon topbar-menu" aria-label="Menu" aria-expanded={menu} onClick={() => setMenu(true)}><IconMenu2 size={22} /></button>
            <Logo />
            <span className="topbar-product">{product}</span>
            <ThemeToggle style={{ color: "var(--nav-text)" }} />
          </header>
          <Drawer open={menu} onOpenChange={setMenu} title="Menu" hideTitle side="left" width={300} className="nav-drawer">{navigation}</Drawer>
          <main className="main">{children}</main>
        </div>
      ) : (
        <div className="shell">
          {navigation}
          <main className="main">{children}</main>
        </div>
      )}
    </KitProviders>
  );
}
